import {
	ConditionType,
	type CurrencyType,
	LocationActionDTO,
	ShipmentLocationActionRequestDtoLocationActionType as LocationActionType,
	type ShipmentRequirementDTO,
	WeightUnit,
} from '@uturn/api/v1';
import {
	type ShipmentDto,
	ShipmentDtoShipmentStatus as ShipmentStatus,
} from '@uturn/api/v2';
import type {
	FormValues,
	ZodLocation,
} from '@uturn/portal/modules/shipments/schema';
import type { OrganizationDefaultValues } from '@uturn/portal/types/shipment';
import {
	ContainerStatus,
	TransportTypeCode,
	TypeOfShipment,
} from '@uturn/portal/types/shipment';
import { removeEmptyDataFields } from '@uturn/portal/utils/remove-empty-data-fields';
import {
	defaultTransportTypeLocationActions,
	fromZodLocations,
	toZodLocations,
} from './locations';
import { guessTransportType } from './transport-type';

export const grossWeightUndefined = 0; // TODO: => null?
export const priceUndefined = 0; // TODO: => null?

/**
 * For some reason setting the object directly does change the field state.
 */
export const shouldUpdate = {
	shouldTouch: true,
	shouldDirty: true,
};

/**
 * For some reason setting the object directly does not trigger the validation
 * so we need to set the values individually and then set shouldValidate to true.
 *
 * Might be a worth to revisit this in the future.
 *
 * ```ts
 * form.setValue(
 *   `locations.${index}`,
 *   {
 *      action: { location: { id: locationId } },
 *      countryCode,
 *   },
 *   { shouldValidate: true }
 * );
 * ```
 */
export const shouldRevalidate = {
	shouldTouch: true,
	shouldDirty: true,
	shouldValidate: true, // Required to remove any validation error
};

export type CopyOptions = {
	shipmentRequirements: ShipmentRequirementDTO[];
	shippingRequirementExceptions: string[];
};

export const fromApi = (data: ShipmentDto): FormValues => {
	const {
		shipmentUnitDescription, // parse for other fields or itself
		requirements,
		locationActions,
		preferredCarriers,
		routes,
		publishUntil,
		shipmentStatus,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		tsoStatus,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		carrier,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		pendingChangeRequest,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		quotes,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		id,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		number,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		shipmentsInBatch,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		batchId,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		group,
		...rest
	} = data;

	return {
		...rest,
		concept: shipmentStatus === ShipmentStatus.CONCEPT,
		containerType: shipmentUnitDescription ?? '',
		transportType: guessTransportType(locationActions),
		typeOfShipment: TypeOfShipment.SINGLE, // group?.name ? TypeOfShipment.GROUP : TypeOfShipment.SINGLE,
		numberOfCopiesToBeCreated: 1, // shipmentsInBatch ?? 1,
		newGroup: undefined, // group?.name ? { name: group.name } : undefined,
		containerStatus: rest.grossWeight?.quantity
			? ContainerStatus.FULL
			: ContainerStatus.EMPTY,
		shippingLineId: rest.shippingLine?.id,
		requirementIds:
			requirements
				?.filter((item) => !!item.id)
				.map((item) => item.id as number) ?? [], // Guarantied by Type Predicate Inference
		locations: toZodLocations(locationActions),
		preferredCarrierIds: preferredCarriers?.map((item) => item.orgId) ?? [],
		routes: routes.routes,
		publishUntilDate: publishUntil ?? '',
	} as FormValues;
};

export const fromForm = (data: Partial<FormValues>, id?: number) => {
	// CreateShipment
	if (!id) {
		const {
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			transportType,
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			typeOfShipment,
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			containerType,
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			containerStatus,
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			invoiceRequired,
			// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
			shippingLine,
			preferredCarrierIds,
			locations,
			...restSubmitData
		} = data;

		const toSubmitData = {
			...restSubmitData,
			preferredCarriersIds: preferredCarrierIds ?? [], // TODO: Remove this when CreateShipment type gets fixed too.
		};

		// Already z.schema transformed
		if (!locations || toSubmitData?.locationActions?.length) {
			return removeEmptyDataFields(toSubmitData);
		}

		const { locationActions } = fromZodLocations(locations);

		const result = removeEmptyDataFields({
			...toSubmitData,
			locationActions,
		});

		return result;
	}

	// UpdateShipment
	const {
		locations,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		concept,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		transportType,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		typeOfShipment,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		containerType,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		containerStatus,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		invoiceRequired,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		shippingLine,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		newGroup,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		numberOfCopiesToBeCreated,
		// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
		routes,
		...toSubmitData
	} = data;

	// Already z.schema transformed
	if (!locations || toSubmitData?.locationActions?.length) {
		return removeEmptyDataFields(toSubmitData);
	}

	const { locationActions } = fromZodLocations(locations);

	const result = removeEmptyDataFields({
		...toSubmitData,
		locationActions,
	});

	return result;
};

export const toStore = (
	transportType: TransportTypeCode,
	typeOfShipment: TypeOfShipment,
	initData: Partial<FormValues>,
	orgDefaultValues: OrganizationDefaultValues,
	currencyCode: CurrencyType,
): Partial<FormValues> => {
	const defaultContainerStatus =
		transportType !== TransportTypeCode.SHUNT
			? ContainerStatus.FULL
			: (initData?.containerStatus ?? ContainerStatus.FULL);
	return {
		...initData,
		concept: true,
		numberOfCopiesToBeCreated: initData.numberOfCopiesToBeCreated ?? 1,
		equipment: {
			containerNumber: initData.equipment?.containerNumber ?? '',
			isoType: initData.equipment?.isoType ?? '',
			shipmentUnitId: initData.equipment?.shipmentUnitId ?? 0,
		},
		grossWeight: {
			quantity:
				initData.grossWeight?.quantity ||
				(defaultContainerStatus === ContainerStatus.EMPTY
					? 0
					: grossWeightUndefined),
			unit: initData.grossWeight?.unit ?? WeightUnit.kilogram,
		},
		requirementIds: initData.requirementIds ?? [],
		locations: defaultTransportTypeLocationActions[
			transportType
		] as Partial<ZodLocation>,
		preferredCarrierIds: initData.preferredCarrierIds ?? [],
		price: {
			quantity: initData.price?.quantity ?? priceUndefined,
			currencyCode: initData.price?.currencyCode ?? currencyCode,
		},
		fixedPrice: initData.fixedPrice ?? true,
		publishUntilDate: initData.publishUntilDate ?? undefined,
		shipperReference: initData.shipperReference ?? '',
		conditions: orgDefaultValues?.conditions ?? ConditionType.AVC,
		transportType,
		typeOfShipment,
		containerStatus: defaultContainerStatus,
		invoiceRequired: orgDefaultValues?.invoiceRequired ?? false,
	} as Partial<FormValues>;
};

export const copyLocations = (data: Partial<FormValues>): ZodLocation[] => {
	const now = new Date();
	return (
		data.locations
			?.filter((item) => !!item.action)
			.map((item) => {
				const action = item.action as LocationActionDTO; // Guarantied by Type Predicate Inference

				const {
					dateFrom,
					dateUntil,
					// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
					id,
					// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
					arrival,
					// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
					departure,
					// biome-ignore lint/correctness/noUnusedVariables: Exclude field.
					eta,
					...rest
				} = action;

				return {
					...item,
					action: {
						...rest,
						id: crypto.randomUUID(),
						dateFrom: dateFrom && new Date(dateFrom) < now ? '' : dateFrom,
						dateUntil: dateUntil && new Date(dateUntil) < now ? '' : dateUntil,
					},
				};
			}) ?? []
	);
};

/**
 * Derive the publish-until date for a copied shipment from its pickup location.
 *
 * A copied shipment must reset publish-until to the pick-up `dateUntil`: when
 * the source shipment was already assigned.
 *  Returns '' when the pickup has no (future) date, since
 * `copyLocations` clears past dates.
 */
export const getPickupDateUntilFromCopiedLocations = (
	locations: ZodLocation[],
): string => {
	const pickup =
		locations.find(
			(item) => item.action?.locationActionType === LocationActionType.PICKUP,
		) ?? locations[0];
	return pickup?.action?.dateUntil ?? '';
};

export const copyRequirements = (
	data: Partial<FormValues>,
	options: CopyOptions,
) => {
	return (
		data.requirementIds
			?.filter((requirementId) => {
				const items = options.shipmentRequirements ?? [];
				if (items.length === 0) {
					return false;
				}
				const itemId = requirementId;
				const itemCode = items.find(
					(item) => item.id && itemId === item.id,
				)?.code;
				if (!itemCode) {
					return false;
				}
				return !options.shippingRequirementExceptions.includes(itemCode);
			})
			.map((requirementId) => requirementId) ?? []
	);
};
